// src/components/SuggestedRow.jsx
import React, { useEffect, useMemo, useState } from 'react';
import { useSuggested } from '../context/SuggestedContext';
import { FALLBACK_IMG } from '../utils/image';
import api from '../api';
import './SuggestedRow.css';

// ---------- helpers (mirror Banner/Department logic) ----------
function getEffectivePricePi(p, piToUsdRate) {
  // Prefer server-enriched shape if present
  if (p?.pricingView && typeof p.pricingView.effectivePrice === 'number') {
    const base = p.pricingView.basePrice;
    const eff = p.pricingView.effectivePrice;
    const isOnSale = !!p.pricingView.isOnFlashSale;
    const percent =
      isOnSale && typeof base === 'number' && base > 0
        ? Math.max(0, Math.min(100, Math.round((1 - eff / base) * 100)))
        : null;
    return { pricePi: eff, isOnSale, percentOff: percent };
  }

  if (typeof p?.effectivePricePi === 'number') {
    const base =
      p?.pricingType === 'dynamic' &&
      typeof p?.usdPrice === 'number' &&
      typeof piToUsdRate === 'number' &&
      piToUsdRate > 0
        ? p.usdPrice / piToUsdRate
        : typeof p?.price === 'number'
        ? p.price
        : null;

    const eff = p.effectivePricePi;
    const isOnSale = !!(p?.sale?.active || p?.isOnSale === true);
    const percent =
      isOnSale && typeof base === 'number' && base > 0
        ? Math.max(0, Math.min(100, Math.round((1 - eff / base) * 100)))
        : null;
    return { pricePi: eff, isOnSale, percentOff: percent };
  }

  // Fallback compute
  const isDynamic = p?.pricingType === 'dynamic' && typeof p?.usdPrice === 'number';
  const basePi =
    isDynamic && typeof piToUsdRate === 'number' && piToUsdRate > 0
      ? p.usdPrice / piToUsdRate
      : typeof p?.price === 'number'
      ? p.price
      : null;

  const sale = p?.sale || p?.flashSale || p?.activeSale || {};
  const saleActive =
    !!sale?.active ||
    typeof p?.activeSalePricePi === 'number' ||
    typeof p?.activeSalePriceUsd === 'number' ||
    typeof p?.salePrice === 'number' ||
    typeof p?.salePricePi === 'number' ||
    typeof p?.salePriceUsd === 'number';

  let salePi = null;

  // explicit Pi first
  if (typeof sale?.pricePi === 'number') salePi = sale.pricePi;
  else if (typeof p?.activeSalePricePi === 'number') salePi = p.activeSalePricePi;
  else if (typeof p?.salePricePi === 'number') salePi = p.salePricePi;

  const usdToPi = (usd) =>
    typeof usd === 'number' && typeof piToUsdRate === 'number' && piToUsdRate > 0
      ? usd / piToUsdRate
      : null;

  if (salePi == null) {
    if (typeof sale?.priceUsd === 'number') salePi = usdToPi(sale.priceUsd);
    else if (typeof p?.activeSalePriceUsd === 'number') salePi = usdToPi(p.activeSalePriceUsd);
    else if (typeof p?.salePriceUsd === 'number') salePi = usdToPi(p.salePriceUsd);
  }

  if (salePi == null && typeof p?.salePrice === 'number') salePi = p.salePrice;

  if (basePi == null) return { pricePi: null, isOnSale: false, percentOff: null };

  if (!saleActive || salePi == null) {
    return { pricePi: basePi, isOnSale: false, percentOff: null };
  }

  const finalSalePi = Math.max(0, +Number(salePi).toFixed(2));
  const percentOff = Math.max(
    0,
    Math.min(100, Math.round((1 - finalSalePi / basePi) * 100))
  );

  return {
    pricePi: finalSalePi,
    isOnSale: true,
    percentOff: Number.isFinite(percentOff) ? percentOff : null,
  };
}

function firstImage(p) {
  const src = Array.isArray(p?.media) && p.media[0] ? p.media[0] : null;
  return src || FALLBACK_IMG;
}
function titleOf(p) {
  return p?.name || p?.title || 'Untitled';
}
// ---------------------------------------------------------------

export default function SuggestedRow({ onOpen }) {
  const { loading, error, products } = useSuggested();

  // Cached Pi→USD rate (needed for fallback compute)
  const [piToUsdRate, setPiToUsdRate] = useState(null);
  useEffect(() => {
    let timer;
    const fetchRate = async () => {
      try {
        const res = await api.get('/convert/rate');
        if (res.data?.piToUsd) setPiToUsdRate(res.data.piToUsd);
      } catch (err) {
        console.error('❌ Failed to fetch cached Pi rate (suggested):', err?.message || err);
      }
    };
    fetchRate();
    timer = setInterval(fetchRate, 600000); // 10 min
    return () => clearInterval(timer);
  }, []);

  const priceLabel = (p) => {
    const { pricePi } = getEffectivePricePi(p, piToUsdRate);
    return pricePi != null ? `@ ${pricePi.toFixed(2)} Pi` : '@ —';
  };

  const saleBadgeText = (p) => {
    const { isOnSale, percentOff } = getEffectivePricePi(p, piToUsdRate);
    if (!isOnSale) return null;
    return typeof percentOff === 'number' && percentOff > 0 ? `-${percentOff}%` : '-SALE';
  };

  if (loading && (!products || products.length === 0)) {
    return (
      <section className="sugg2">
        <div className="sugg2__header"><h3 className="sugg2__title">Suggested for you</h3></div>
        <div className="sugg2__status">Loading suggestions…</div>
      </section>
    );
  }
  if (error && (!products || products.length === 0)) {
    return (
      <section className="sugg2">
        <div className="sugg2__header"><h3 className="sugg2__title">Suggested for you</h3></div>
        <div className="sugg2__status">{error}</div>
      </section>
    );
  }
  if (!products?.length) return null;

  return (
    <section className="sugg2">
      <div className="sugg2__header">
        <h3 className="sugg2__title">Suggested for you</h3>
      </div>

      <div className="sugg2__scroller" role="list">
        {products.map((p) => {
          const src = firstImage(p);
          const title = titleOf(p);
          const badge = saleBadgeText(p);

          return (
            <button
              key={p._id}
              className="sugg2-card"
              role="listitem"
              onClick={() => onOpen?.(p)}
              aria-label={`View ${title}`}
              title={title}
            >
              <div className="sugg2-card__imgwrap">
                <img
                  className="sugg2-card__img"
                  src={src}
                  alt={title}
                  loading="lazy"
                  onError={(e) => {
                    e.currentTarget.onerror = null;
                    e.currentTarget.src = FALLBACK_IMG;
                  }}
                />
                {/* 🔴 Top-left sale badge (just like BannerResults) */}
                {badge && <span className="sugg2-chip sugg2-chip--sale">{badge}</span>}
              </div>

              <div className="sugg2-card__name">{title}</div>

              <div className="sugg2-card__meta">
                {/* ✅ Replace original price with sale price when active */}
                <div className="sugg2-card__price">{priceLabel(p)}</div>
                {p?.expectedDeliveryDays && (
                  <div className="sugg2-card__delivery">{p.expectedDeliveryDays}d</div>
                )}
              </div>
            </button>
          );
        })}
      </div>
    </section>
  );
}
